import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Cpu, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ControlPanel from "@/components/smarttalk/ControlPanel";
import LiveTranscript from "@/components/smarttalk/LiveTranscript";
import TranslationPanel from "@/components/smarttalk/TranslationPanel";
import { useSpeechSynthesis } from "@/components/VoiceController";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function getWSUrl() {
  const base = API || "";
  const wsBase = base.replace(/^http/, "ws");
  return `${wsBase}/ws/smart-talk/live`;
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

export default function SmartTalk() {
  const navigate = useNavigate();

  // Language state
  const [sourceLang, setSourceLang] = useState("auto");
  const [targetLang, setTargetLang] = useState("off");

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [status, setStatus] = useState("idle");
  const [duration, setDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);

  // Transcript state
  const [segments, setSegments] = useState([]);
  const [partialText, setPartialText] = useState("");
  const [partialTranslation, setPartialTranslation] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [confidence, setConfidence] = useState(null);
  const [fullText, setFullText] = useState("");

  // TTS & QC state
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [qcEnabled, setQcEnabled] = useState(true);
  const { voices, speaking, speak, stop: stopTts } = useSpeechSynthesis();

  // Refs
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const segmentsRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const isRecordingRef = useRef(false);
  const targetLangRef = useRef(targetLang);
  const sourceLangRef = useRef(sourceLang);
  const ttsEnabledRef = useRef(ttsEnabled);
  const qcEnabledRef = useRef(qcEnabled);

  // Keep refs in sync
  useEffect(() => { targetLangRef.current = targetLang; }, [targetLang]);
  useEffect(() => { sourceLangRef.current = sourceLang; }, [sourceLang]);
  useEffect(() => { ttsEnabledRef.current = ttsEnabled; }, [ttsEnabled]);
  useEffect(() => { qcEnabledRef.current = qcEnabled; }, [qcEnabled]);

  // ── Audio Capture ──
  const startAudioCapture = useCallback((stream, ws) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (event) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const inputData = event.inputBuffer.getChannelData(0);
        // Calculate audio level
        let sum = 0;
        for (let i = 0; i < inputData.length; i++) sum += Math.abs(inputData[i]);
        setAudioLevel(Math.min(1, sum / inputData.length * 3));

        // Convert to PCM s16le
        const pcm = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        ws.send(pcm.buffer);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
    } catch (err) {
      toast.error("Audio capture setup failed");
      cleanup();
    }
  }, []);

  // ── WebSocket ──
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      setStatus("connecting");
      setSegments([]);
      segmentsRef.current = [];
      setPartialText("");
      setPartialTranslation("");
      setFullText("");
      setWordCount(0);

      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      const sr = audioContext.sampleRate;
      audioContextRef.current = audioContext;

      const ws = new WebSocket(getWSUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        const token = localStorage.getItem("token") || "";
        ws.send(JSON.stringify({
          type: "config",
          sample_rate: sr,
          source_language: sourceLangRef.current,
          target_language: targetLangRef.current === "off" ? "" : targetLangRef.current,
          quality_control: qcEnabledRef.current,
          tts_enabled: ttsEnabledRef.current,
          token,
        }));
        setStatus("recording");
        setIsRecording(true);
        isRecordingRef.current = true;
        setDuration(0);
        toast.success("Smart Talk listening...");
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "transcript") {
            const segment = {
              id: `seg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
              text: msg.text,
              raw_text: msg.raw_text || "",
              translation: msg.translation || "",
              speaker: msg.speaker || `Speaker ${msg.speaker_id || 0}`,
              is_final: msg.is_final || false,
              start_time: msg.start_time,
              end_time: msg.end_time,
              confidence: msg.confidence,
              quality_control: msg.quality_control || null,
              tts_ready: msg.tts_ready || false,
            };

            if (msg.is_final) {
              segmentsRef.current = [...segmentsRef.current, segment];
              setSegments(segmentsRef.current);
              setPartialText("");
              setPartialTranslation("");
              setWordCount(wc => wc + (msg.text || "").split(/\s+/).filter(Boolean).length);
              if (msg.confidence != null) setConfidence(Math.round(msg.confidence * 100));

              // Auto-TTS: speak translation if available, else speak cleaned text
              if (ttsEnabledRef.current && msg.text && !speaking) {
                const ttsText = msg.translation || msg.text;
                const ttsLang = msg.target_language || "en";
                const langVoice = voices.find(v => v.lang && v.lang.startsWith(ttsLang.slice(0, 2))) || null;
                speak(ttsText, { voice: langVoice, rate: 1.0 });
              }
            } else {
              setPartialText(msg.text || "");
              setPartialTranslation(msg.translation || "");
            }
          } else if (msg.type === "session_started") {
            startAudioCapture(stream, ws);
          } else if (msg.type === "completed") {
            setFullText(msg.text || "");
            setStatus("completed");
          } else if (msg.type === "debug") {
            // silent
          } else if (msg.type === "error") {
            toast.error(msg.detail || "Smart Talk error");
            setStatus("error");
          }
        } catch {}
      };

      ws.onerror = () => {
        toast.error("WebSocket connection failed");
        setStatus("error");
        cleanup();
      };

      ws.onclose = () => {
        if (isRecordingRef.current) cleanup();
      };

      recordingTimerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);

    } catch (err) {
      toast.error("Could not access microphone");
      setStatus("idle");
    }
  }, [startAudioCapture]);

  const pauseRecording = () => {
    setIsPaused(true);
    setStatus("paused");
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "pause" }));
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const resumeRecording = () => {
    setIsPaused(false);
    setStatus("recording");
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "resume" }));
    }
    recordingTimerRef.current = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);
  };

  const stopRecording = () => {
    stopTts();
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "stop" }));
    }
    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);
    setStatus("completed");
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    cleanupAudio();
    toast.info("Processing transcription...");
  };

  const cleanup = () => {
    cleanupAudio();
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
    isRecordingRef.current = false;
    setStatus("idle");
  };

  const cleanupAudio = () => {
    if (processorRef.current && audioContextRef.current) {
      try { processorRef.current.disconnect(); } catch {}
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
          wsRef.current.close();
        }
      } catch {}
    }
    audioContextRef.current = null;
    processorRef.current = null;
    streamRef.current = null;
    wsRef.current = null;
  };

  useEffect(() => {
    return () => cleanup();
  }, []);

  // ── File Upload ──
  const handleFileUpload = async (file) => {
    if (!file) return;
    setStatus("loading");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("language_code", sourceLang === "auto" ? "" : sourceLang);
      form.append("target_language", targetLang === "off" ? "" : targetLang);
      const res = await axios.post(`${API}/api/smart-talk/transcribe`, form, {
        headers: getHeaders(), withCredentials: true,
      });
      const data = res.data;
      const seg = {
        id: "file_upload",
        text: data.text,
        translation: data.translated_text || "",
        speaker: "Speaker 1",
        is_final: true,
        start_time: 0,
        end_time: data.duration_seconds,
      };
      segmentsRef.current = [seg];
      setSegments([seg]);
      setFullText(data.text);
      setWordCount((data.text || "").split(/\s+/).filter(Boolean).length);
      setStatus("completed");
      toast.success("Transcription complete!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Smart Talk failed");
      setStatus("error");
    }
  };

  // ── Copy & Export ──
  const handleCopyAll = () => {
    const texts = segments.map(s => `${s.speaker}: ${s.text}`).join("\n");
    if (!texts) return toast.error("Nothing to copy");
    navigator.clipboard.writeText(texts);
    toast.success("Copied to clipboard");
  };

  const handleExport = async () => {
    // If we have a completed transcription, save it first then export
    if (!segments.length) return toast.error("Nothing to export");
    const text = segments.map(s => s.text).join(" ");
    try {
      const form = new FormData();
      form.append("file", new Blob([text], { type: "text/plain" }), "smart_talk_transcript.txt");
      // For now, trigger a download
      const blob = new Blob([segments.map(s => `[${s.start_time?.toFixed(1) || "0.0"}s] ${s.speaker}: ${s.text}${s.translation ? `\n→ ${s.translation}` : ""}`).join("\n")], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smart-talk-${new Date().toISOString().slice(0, 10)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported as TXT");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#0A0D14" }}>
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/60 backdrop-blur px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/dashboard")} className="text-gray-400 hover:text-white p-1">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-lg flex items-center justify-center">
          <Cpu className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-['JetBrains_Mono'] text-lg font-bold text-white tracking-tight">SMART TALK</h1>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
              status === "recording" || status === "connected" ? "bg-green-900/40 text-green-400" :
              status === "connecting" ? "bg-amber-900/40 text-amber-400" :
              status === "error" ? "bg-red-900/40 text-red-400" :
              "bg-gray-800 text-gray-500"
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                status === "recording" || status === "connected" ? "bg-green-400 animate-pulse" :
                status === "connecting" ? "bg-amber-400" :
                status === "error" ? "bg-red-400" : "bg-gray-500"
              }`} />
              {status === "recording" ? "LIVE" :
               status === "connected" ? "CONNECTED" :
               status === "connecting" ? "CONNECTING" :
               status === "completed" ? "DONE" :
               status === "error" ? "ERROR" : "READY"}
            </span>
            {isRecording && (
              <span className="text-[11px] font-mono text-cyan-400">{formatTime(duration)}</span>
            )}
          </div>
          <p className="text-[11px] text-gray-500 font-['IBM_Plex_Sans']">Sovereign Multilingual Intelligence Hub</p>
        </div>
      </div>

      {/* Three-panel layout */}
      <div className="flex flex-col lg:flex-row gap-0" style={{ height: "calc(100vh - 57px)" }}>
        {/* Left Panel — Controls */}
        <div className="w-full lg:w-64 xl:w-72 bg-gray-900/40 border-r border-gray-800 p-4 overflow-y-auto">
          <ControlPanel
            sourceLang={sourceLang}
            setSourceLang={setSourceLang}
            targetLang={targetLang}
            setTargetLang={setTargetLang}
            isRecording={isRecording}
            isPaused={isPaused}
            onStart={startRecording}
            onStop={stopRecording}
            onPause={pauseRecording}
            onResume={resumeRecording}
            onFileUpload={handleFileUpload}
            onCopyAll={handleCopyAll}
            onExport={handleExport}
            status={status}
            confidence={confidence}
            wordCount={wordCount}
            duration={formatTime(duration)}
            audioLevel={audioLevel}
            ttsEnabled={ttsEnabled}
            onTtsToggle={() => setTtsEnabled(!ttsEnabled)}
            qcEnabled={qcEnabled}
            onQcToggle={() => setQcEnabled(!qcEnabled)}
          />
        </div>

        {/* Middle Panel — Live Transcript */}
        <div className="flex-1 bg-gray-900/20 border-r border-gray-800 p-4 overflow-hidden">
          <LiveTranscript
            segments={segments}
            partialText={partialText}
            isRecording={isRecording}
          />
        </div>

        {/* Right Panel — Translation */}
        <div className="w-full lg:w-96 xl:w-[420px] bg-gray-900/20 p-4 overflow-hidden">
          <TranslationPanel
            segments={segments}
            targetLang={targetLang}
            partialTranslation={partialTranslation}
            onPlayTts={(text, lang) => {
              if (!text) return;
              const langVoice = voices.find(v => v.lang && v.lang.startsWith((lang || "en").slice(0, 2))) || null;
              speak(text, { voice: langVoice, rate: 1.0 });
            }}
          />
        </div>
      </div>
    </div>
  );
}
